import { IUser } from '@/interfaces/IUser';
import { defineStore } from 'pinia';
import {
  computed,
  onBeforeUnmount,
  reactive,
  toRef,
  toRefs,
  watch,
  watchEffect,
} from 'vue';
import { useSocket } from './use-socket';
import { IMember, MemberRole } from '@/interfaces/IMember';
import { PermissionType } from '@/enums/PermissionType';
import axios, { AxiosError } from 'axios';
import { ExceptionType } from '@/enums/ExceptionType';
import { useModal } from './use-modal';
import { IAuthData } from '@/interfaces/IAuthData';
import ModalAdminPin from '@/components/modals/ModalAdminPin.vue';
import { getServerErrorMessage } from '@/utils/error.utils';

export const useAuthStore = defineStore('auth', () => {
  const state = reactive({
    user: null as IUser | null,
    currentRole: null as IMember | null,
    permissions: null as number | null,
  });

  const actions = {
    setUser(user: IUser | null) {
      state.user = user;
    },
    setCurrentRole(role: IMember | null) {
      state.currentRole = role;
    },
    setPermissions(permissions: number | null) {
      state.permissions = permissions;
    },
  };

  const getters = {
    permissionSet: computed(() => {
      const set = new Set<number>();
      if (
        state.currentRole?.type === MemberRole.Admin ||
        state.currentRole?.type === MemberRole.Owner
      ) {
        // Return all permissions in PermissionType
        for (let item in PermissionType) {
          if (!isNaN(Number(item))) {
            set.add(Number(item));
          }
        }
      }

      // extract each set bit from the permissions number
      let permissions = state.permissions;
      let index = 0;
      while (permissions) {
        if (permissions & 1) {
          set.add(1 << index);
        }
        permissions >>= 1;
        index++;
      }
      return set;
    }),
  };

  return {
    ...toRefs(state),
    ...actions,
    ...getters,
  };
});

export function useAuth(main = false) {
  const store = useAuthStore();
  const socketHelper = useSocket();

  if (main) {
    const modals = useModal();

    watch(
      () => store.user,
      (newUser, oldUser) => {
        if (newUser) {
          socketHelper.connect();
        } else {
          socketHelper.disconnect();
        }
      },
      { immediate: true }
    );

    const interceptor = axios.interceptors.response.use(
      undefined,
      async function (error: AxiosError): Promise<never> {
        if (error.response?.status === 403) {
          const rawMessage =
            typeof error.response.data?.message === 'string'
              ? error.response.data.message
              : undefined;

          // We keep the raw string to parse internal $$ metadata.
          // For all other shapes, compute a safe user-facing message (used by callers in catch blocks).
          const message = rawMessage
            ? rawMessage
            : getServerErrorMessage(error, { defaultMessage: 'No autorizado' });

          if (typeof message === 'string' && message.startsWith('$$')) {
            //$$errorKey:message
            const [errorKey, errorMessage] = message.slice(2).split(':', 2);

            switch (errorKey) {
              case ExceptionType.AdminAuthRequired:
                const previousData = (() => {
                  const data = error.config?.data;
                  if (!data) return undefined;
                  if (typeof data === 'string') {
                    try {
                      return JSON.parse(data);
                    } catch {
                      return undefined;
                    }
                  }
                  if (typeof data === 'object') return data;
                  return undefined;
                })();

                if (previousData && !previousData.authData) {
                  const [permission, permissionMessage] = errorMessage.split(
                    '$$',
                    2
                  );

                  const authData = await modals.showDynamicDialog<IAuthData>(
                    ModalAdminPin,
                    {
                      requiredPermission: +permission,
                      // Audit reason: the permission-derived message the
                      // server already sends with the 403.
                      reason: permissionMessage || undefined,
                    },
                    'admin-pin'
                  );

                  if (authData) {
                    // Add authData to the body of the request and retry
                    const newData = {
                      ...previousData,
                      authData,
                    };

                    const newConfig = {
                      ...error.config,
                      data: JSON.stringify(newData),
                    };

                    return axios.request(newConfig);
                  }
                }
                break;
            }
          }
        }

        throw error;
      }
    );

    onBeforeUnmount(() => {
      axios.interceptors.response.eject(interceptor);
    });
  }

  function hasPermission(permission: PermissionType): boolean {
    return store.permissionSet?.has?.(permission) ?? false;
  }

  return {
    user: toRef(store, 'user'),
    setUser: store.setUser,
    hasPermission,
  };
}
